import { reactive, ref, computed } from "#imports";
import {
  findClosestBlock,
  buildDraggableItem,
  falsy,
  mapDroppableField,
  findClosestEntityContext
} from "#blokkli/helpers";
import { cloneElementWithStyles } from "./dom/index.js";
import onBlokkliEvent from "./composables/onBlokkliEvent.js";
import useDelayedIntersectionObserver from "./composables/useDelayedIntersectionObserver.js";
import { getDefinition } from "#blokkli-build/definitions";
const buildFieldElement = (element) => {
  const key = element.dataset.fieldKey;
  const name = element.dataset.fieldName;
  const label = element.dataset.fieldLabel;
  const isNested = element.dataset.fieldIsNested === "true";
  const nestingLevel = Number.parseInt(element.dataset.bkNestingLevel || "0");
  const fieldListType = element.dataset.fieldListType;
  const hostEntityType = element.dataset.hostEntityType;
  const hostEntityBundle = element.dataset.hostEntityBundle;
  const hostEntityUuid = element.dataset.hostEntityUuid;
  const dropAlignment = element.dataset.fieldDropAlignment;
  const cardinality = Number.parseInt(element.dataset.fieldCardinality || "-1");
  const allowedBundles = (element.dataset.fieldAllowedBundles || "").split(",").filter(Boolean);
  const allowedFragments = (element.dataset.allowedFragments || "").split(",").filter(Boolean);
  if (key && name && label && hostEntityType && hostEntityUuid && hostEntityBundle && fieldListType) {
    return {
      key,
      name,
      label,
      isNested,
      nestingLevel,
      hostEntityType,
      hostEntityUuid,
      hostEntityBundle,
      cardinality: Number.isNaN(cardinality) ? -1 : cardinality,
      allowedBundles,
      allowedFragments,
      fieldListType,
      element,
      dropAlignment: dropAlignment === "vertical" || dropAlignment === "horizontal" ? dropAlignment : void 0
    };
  }
};
function rectWithTime(rect, time) {
  return {
    ...rect,
    time: time || performance.now()
  };
}
export default function(ui, debug) {
  const artboardElement = ui.artboardElement();
  const logger = debug.createLogger("DomProvider");
  const mutationsReady = ref(true);
  const intersectionReady = ref(false);
  const visibleBlocks = /* @__PURE__ */ new Set();
  const visibleFields = /* @__PURE__ */ new Set();
  const blockRects = {};
  const fieldRects = {};
  let draggableBlockCache = {};
  const resizeObserver = new ResizeObserver(function(entries) {
    for (const entry of entries) {
      if (!(entry.target instanceof HTMLElement)) {
        return;
      }
      const uuid = entry.target.dataset.uuid || entry.target.closest("[data-uuid]")?.dataset.uuid;
      if (!uuid) {
        return;
      }
      const currentRect = blockRects[uuid];
      const now = performance.now();
      if (!currentRect) {
        blockRects[uuid] = rectWithTime(
          {
            x: entry.contentRect.x,
            y: entry.contentRect.y,
            width: entry.borderBoxSize[0].inlineSize,
            height: entry.borderBoxSize[0].blockSize
          },
          now
        );
        return;
      }
      if (currentRect.time > now) {
        return;
      }
      blockRects[uuid].width = entry.borderBoxSize[0].inlineSize;
      blockRects[uuid].height = entry.borderBoxSize[0].blockSize;
      blockRects[uuid].time = now;
    }
  });
  function intersectionCallback(entries) {
    const scale = ui.artboardScale.value;
    const offset = ui.artboardOffset.value;
    for (const entry of entries) {
      if (entry.target instanceof HTMLElement) {
        const uuid = entry.target.dataset.uuid || entry.target.closest("[data-uuid]")?.dataset.uuid;
        const fieldKey = entry.target.dataset.fieldKey;
        const rect = entry.target.getBoundingClientRect();
        if (fieldKey) {
          if (entry.isIntersecting) {
            visibleFields.add(fieldKey);
          } else {
            visibleFields.delete(fieldKey);
          }
          fieldRects[fieldKey] = ui.getAbsoluteElementRect(rect, scale, offset);
        } else if (uuid) {
          const newRect = ui.getAbsoluteElementRect(rect, scale, offset);
          const currentRect = blockRects[uuid];
          if (currentRect) {
            if (currentRect.time > entry.time) {
              blockRects[uuid].x = newRect.x;
              blockRects[uuid].y = newRect.y;
            } else {
              blockRects[uuid] = rectWithTime(newRect, entry.time);
            }
          } else {
            blockRects[uuid] = rectWithTime(newRect, entry.time);
          }
          if (entry.isIntersecting) {
            visibleBlocks.add(uuid);
          } else {
            visibleBlocks.delete(uuid);
          }
        }
      }
    }
  }
  const intersectionObserver = useDelayedIntersectionObserver(intersectionCallback);
  const registeredBlocks = reactive({});
  const registeredFields = reactive({});
  const registeredFieldTypes = computed(() => {
    const fields = Object.values(registeredFields);
    const found = /* @__PURE__ */ new Set();
    const uniqueFieldTypes = [];
    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      if (field) {
        const key = `${field.entity.type}:${field.entity.bundle}:${field.fieldName}`;
        if (!found.has(key)) {
          uniqueFieldTypes.push({
            entityType: field.entity.type,
            entityBundle: field.entity.bundle,
            fieldName: field.fieldName
          });
          found.add(key);
        }
      }
    }
    return uniqueFieldTypes;
  });
  const registerField = (entity, fieldName, element) => {
    const key = `${entity.uuid}:${fieldName}`;
    registeredFields[key] = { element, entity, fieldName };
    intersectionObserver.observe(element);
  };
  const updateFieldElement = (entity, fieldName, element) => {
    const key = `${entity.uuid}:${fieldName}`;
    const existingElement = registeredFields[key]?.element;
    if (existingElement) {
      intersectionObserver.unobserve(existingElement);
    }
    registeredFields[key] = { entity, fieldName, element };
    intersectionObserver.observe(element);
  };
  const unregisterField = (entity, fieldName) => {
    const key = `${entity.uuid}:${fieldName}`;
    const el = registeredFields[key]?.element;
    if (el) {
      intersectionObserver.unobserve(el);
    }
    visibleFields.delete(key);
    registeredFields[key] = void 0;
  };
  function getElementToObserve(el, bundle, fieldListType, parentBlockBundle) {
    if (el.classList.contains("bk-block-proxy")) {
      return el;
    }
    const definition = getDefinition(bundle, fieldListType, parentBlockBundle);
    if (!definition) {
      throw new Error("Failed to load definition for bundle: " + bundle);
    }
    const observableElement = (definition.editor?.getDraggableElement ? definition.editor.getDraggableElement(el) : el) || el;
    if (observableElement instanceof HTMLElement) {
      return observableElement;
    }
    return el;
  }
  const findBlock = (uuid) => {
    const cached = draggableBlockCache[uuid];
    if (cached) {
      return cached;
    }
    const el = registeredBlocks[uuid];
    if (!el) {
      return;
    }
    const item = buildDraggableItem(el);
    if (item?.itemType === "existing") {
      draggableBlockCache[uuid] = item;
      return item;
    }
  };
  const getAllBlocks = () => {
    return [
      ...document.querySelectorAll(
        '[data-blokkli-provider-active="true"] [data-uuid]'
      )
    ].map((v) => {
      const item = buildDraggableItem(v);
      if (item?.itemType === "existing") {
        return item;
      }
    }).filter(falsy);
  };
  const getDropElementMarkup = (item, checkSize) => {
    const el = item.itemType === "existing" ? getDragElement(item) : item.element();
    if (!el) {
      return "";
    }
    const dropElement = el.querySelector(".bk-drop-element") || el;
    const childCount = dropElement.querySelectorAll("*").length;
    if (checkSize && childCount > 80) {
      return "";
    }
    return cloneElementWithStyles(dropElement, true).replace(
      /\sdata-\w+="[^"]*"/g,
      ""
    );
  };
  const findField = (uuid, fieldName) => {
    const el = document.querySelector(
      `[data-field-name="${fieldName}"][data-host-entity-uuid="${uuid}"]`
    );
    if (!(el instanceof HTMLElement)) {
      return;
    }
    return buildFieldElement(el);
  };
  const getAllDroppableFields = () => [...document.querySelectorAll("[data-blokkli-droppable-field]")].filter((el) => {
    return !el.closest('[data-bk-in-proxy="true"]');
  }).map(mapDroppableField);
  const getVisibleBlocks = () => Array.from(visibleBlocks);
  const getVisibleFields = () => Array.from(visibleFields);
  const getActiveProviderElement = () => {
    const el = document.querySelector('[data-blokkli-provider-active="true"]');
    if (!el) {
      throw new Error("Failed to find active <BlokkliProvider> element.");
    }
    if (!(el instanceof HTMLElement)) {
      throw new TypeError(
        "The root element of the active <BlokkliProvider> is not an HTMLElement."
      );
    }
    return el;
  };
  function getBlockRects() {
    return blockRects;
  }
  function getBlockRect(uuid) {
    return blockRects[uuid];
  }
  function getFieldRect(key) {
    return fieldRects[key];
  }
  function refreshBlockRect(uuid) {
    const block = findBlock(uuid);
    if (!block) {
      return;
    }
    const el = getDragElement(block);
    if (!el) {
      return;
    }
    blockRects[uuid] = rectWithTime(
      ui.getAbsoluteElementRect(el.getBoundingClientRect())
    );
  }
  function refreshFieldRect(key) {
    const el = document.querySelector(
      `.bk-draggable-list-container[data-field-key="${key}"]`
    );
    if (!(el instanceof HTMLElement)) {
      return;
    }
    fieldRects[key] = ui.getAbsoluteElementRect(el.getBoundingClientRect());
  }
  let stateReloadTimeout = null;
  function getUuidsToUpdateRectsFor() {
    const allUuids = Object.keys(registeredBlocks);
    if (allUuids.length < 150) {
      return allUuids;
    }
    return getVisibleBlocks();
  }
  function updateVisibleRects() {
    const toUpdate = getUuidsToUpdateRectsFor();
    const offset = ui.artboardOffset.value;
    const scale = ui.artboardScale.value;
    for (let i = 0; i < toUpdate.length; i++) {
      const uuid = toUpdate[i];
      const el = registeredBlocks[uuid];
      if (!el) {
        continue;
      }
      const bundle = el.dataset.itemBundle;
      const hostBundle = el.dataset.hostBundle;
      const hostFieldListType = el.dataset.hostFieldListType;
      if (!bundle || !hostFieldListType) {
        continue;
      }
      const observableElement = getElementToObserve(
        el,
        bundle,
        hostFieldListType,
        hostBundle
      );
      blockRects[uuid] = rectWithTime(
        ui.getAbsoluteElementRect(
          observableElement.getBoundingClientRect(),
          scale,
          offset
        )
      );
    }
    const visibleFieldKeys = getVisibleFields();
    for (let i = 0; i < visibleFieldKeys.length; i++) {
      const key = visibleFieldKeys[i];
      const field = registeredFields[key];
      if (!field) {
        continue;
      }
      fieldRects[key] = ui.getAbsoluteElementRect(
        field.element.getBoundingClientRect(),
        scale,
        offset
      );
    }
  }
  onBlokkliEvent("state:reloaded", () => {
    draggableBlockCache = {};
    if (stateReloadTimeout) {
      window.clearTimeout(stateReloadTimeout);
    }
    stateReloadTimeout = window.setTimeout(updateVisibleRects, 300);
  });
  onBlokkliEvent("ui:resized", function() {
    updateVisibleRects();
    getVisibleFields().forEach(refreshFieldRect);
    logger.log("Refreshed all visible rects");
  });
  function init() {
    intersectionObserver.init();
    intersectionReady.value = true;
    logger.log("IntersectionObserver initialized");
  }
  const dragElementUuidMap = /* @__PURE__ */ new WeakMap();
  const dragElementCache = /* @__PURE__ */ new Map();
  function handleNodeAdded(node) {
    if (!(node instanceof HTMLElement)) {
      return;
    }
    if (node.dataset.uuid) {
      const item = buildDraggableItem(node);
      if (item && item.itemType === "existing") {
        const observableElement = getElementToObserve(
          node,
          item.itemBundle,
          item.hostFieldListType,
          item.hostBundle
        );
        intersectionObserver.observe(observableElement);
        resizeObserver.observe(observableElement);
        registeredBlocks[item.uuid] = node;
      }
    } else if (node.dataset.fieldName && node.dataset.fieldKey && node.dataset.fieldCardinality) {
      const blocks = node.querySelectorAll('[data-element-type="existing"]');
      for (const block of blocks) {
        handleNodeAdded(block);
      }
    }
  }
  function handleNodeRemoved(node) {
    if (node instanceof HTMLElement && node.dataset.uuid) {
      const uuid = node.dataset.uuid;
      const el = registeredBlocks[uuid];
      if (el !== node) {
        return;
      }
      if (el) {
        intersectionObserver.unobserve(el);
        resizeObserver.unobserve(el);
        dragElementUuidMap.delete(el);
      }
      dragElementUuidMap.delete(node);
      dragElementCache.delete(uuid);
      registeredBlocks[uuid] = void 0;
      delete blockRects[uuid];
      visibleBlocks.delete(uuid);
    }
  }
  const mutationObserverCallback = function(mutationsList) {
    for (const mutation of mutationsList) {
      if (mutation.type === "childList") {
        for (const node of mutation.removedNodes) {
          handleNodeRemoved(node);
        }
        for (const node of mutation.addedNodes) {
          handleNodeAdded(node);
        }
      }
    }
  };
  const mutationObserver = new MutationObserver(mutationObserverCallback);
  mutationObserver.observe(artboardElement, {
    subtree: true,
    childList: true
  });
  function getDragElement(block) {
    const cached = dragElementCache.get(block.uuid);
    if (cached && document.body.contains(cached)) {
      return cached;
    }
    const el = block.element();
    if (!el) {
      return;
    }
    if (el.parentNode) {
      mutationObserver.observe(el.parentNode, { childList: true });
    }
    dragElementUuidMap.set(el, block.uuid);
    dragElementCache.set(block.uuid, el);
    return el;
  }
  function isBlockVisible(uuid) {
    return visibleBlocks.has(uuid);
  }
  return {
    findBlock,
    getAllBlocks,
    findClosestBlock,
    getDropElementMarkup,
    findField,
    getAllDroppableFields,
    findClosestEntityContext,
    getVisibleBlocks,
    getVisibleFields,
    registerField,
    unregisterField,
    updateFieldElement,
    getActiveProviderElement,
    getBlockRects,
    getBlockRect,
    getFieldRect,
    refreshBlockRect,
    isBlockVisible,
    isReady: computed(() => mutationsReady.value && intersectionReady.value),
    init,
    getDragElement,
    updateVisibleRects,
    registeredFieldTypes
  };
}
